import { enqueueSnackbar } from 'notistack';
import useSWRMutation from 'swr/mutation';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { axFetch } from '/@/store/session/actions';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { invalidate } from '/@/utils/swr';
import type { YieldEstimationAPI } from '/@/views/fields/field-monitoring/components/yield-estimation-form';

export const getCropMonitoringById = async (id: number) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/crop-monitoring/${id}/`, window.location.origin);
    return (await axFetch.get(apiUrl).then((res) => res.data)) as CropMonitoring;
};

export async function getCropMonitorings({
    appInstanceId,
    fieldId,
    pageSize,
    page,
    node,
    includeChildren,
    excludeTargetNode,
    crop,
    year,
    fieldName,
    productName,
    phenology,
    active,
    ordering,
    campaign,
}: {
    appInstanceId: string;
    fieldId?: string;
    pageSize?: number;
    page?: number;
    node?: number;
    includeChildren?: boolean;
    excludeTargetNode?: boolean;
    crop?: 'corn' | 'fallow';
    year?: number;
    fieldName?: string;
    productName?: string;
    phenology?: string;
    active?: boolean;
    ordering?: string;
    campaign?: number;
}): Promise<{
    results: CropMonitoring[];
    count: number;
}> {
    let url = `${settings.urls.apiDomain}/core/v1/crop-monitoring/?app_instance_id=${appInstanceId}`;
    if (pageSize) {
        url += `&page_size=${pageSize}`;
    }
    if (fieldId) {
        url += `&field_id=${encodeURIComponent(fieldId)}`;
    }
    if (page) {
        url += `&page=${page}`;
    }
    if (node) {
        url += `&node=${node}`;
    }
    if (includeChildren !== undefined) url += `&include_children=${includeChildren}`;
    if (excludeTargetNode !== undefined) url += `&exclude_target_node=${excludeTargetNode}`;
    if (crop) {
        url += `&crop=${crop}`;
    }
    if (year) {
        url += `&year=${year}`;
    }
    if (fieldName) {
        url += `&field_name=${fieldName}`;
    }
    if (productName) {
        url += `&product_name=${productName}`;
    }
    if (phenology) {
        url += `&phenology=${phenology}`;
    }
    if (active !== undefined) {
        url += `&active=${active}`;
    }
    if (ordering) {
        url += `&ordering=${ordering}`;
    }
    if (campaign) {
        url += `&campaign=${campaign}`;
    }
    const res = await axFetch.get(url);
    return res.data;
}

export const addCropMonitoringStage = async (cropMonitoringId: number, phenology: string) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/stage/`,
        window.location.origin
    );
    return await axFetch
        .post(apiUrl, {
            phenology: phenology,
            date: new Date().toJSON().slice(0, 10),
        })
        .then((res) => res.data);
};

/** Create a new yield estimation for a campaign (a campaign can have several). */
export const createYieldEstimation = async (form: YieldEstimationAPI) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/crop-monitoring/${form.cropMonitoring}/yield-estimations/`,
        window.location.origin
    );
    return await axFetch.post<YieldEstimationAPI>(String(apiUrl), form).then((res) => res.data);
};

export const useCreateYieldEstimationMutation = ({ cropMonitoringId }: { cropMonitoringId: number }) =>
    useSWRMutation(
        { key: 'createYieldEstimation', cropMonitoringId },
        (key, { arg }: { arg: Parameters<typeof createYieldEstimation>['0'] }) => createYieldEstimation(arg),
        {
            onSuccess: () => {
                invalidate({ key: 'useCropMonitoringById', id: cropMonitoringId });
            },
        }
    );

/** Update an existing yield estimation by id (used to persist the draft as `saved`). */
export const updateYieldEstimation = async (form: YieldEstimationAPI & { id: number }) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/crop-monitoring/${form.cropMonitoring}/yield-estimations/${form.id}/`,
        window.location.origin
    );
    return await axFetch.put<YieldEstimationAPI>(String(apiUrl), form).then((res) => res.data);
};

export const useUpdateYieldEstimationMutation = ({ cropMonitoringId }: { cropMonitoringId: number }) =>
    useSWRMutation(
        { key: 'updateYieldEstimation', cropMonitoringId },
        (key, { arg }: { arg: Parameters<typeof updateYieldEstimation>['0'] }) => updateYieldEstimation(arg),
        {
            onSuccess: () => {
                invalidate({ key: 'useCropMonitoringById', id: cropMonitoringId });
            },
        }
    );

export const generateReportPDF = async ({
    cropMonitoringId,
    language,
}: {
    cropMonitoringId: number;
    language: string;
    fileName?: string;
}) => {
    try {
        const params = new URLSearchParams({
            appDomain: location.origin,
            language,
            cropMonitoringId: cropMonitoringId.toString(),
        });
        enqueueSnackbar(_('Generando PDF...'), {
            variant: 'info',
        });

        const apiUrl = new URL(
            `${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/report/pdf/?${params.toString()}`,
            window.location.origin
        );

        const response = await axFetch.post(apiUrl.toString());

        return response.data;
    } catch (error) {
        enqueueSnackbar(_('Hubo un error al intentar descargar el PDF'), {
            variant: 'error',
        });
        throw error;
    }
};
